import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useResource } from '../lib/useResource';
import type { DeviceDetail, Group } from '../lib/types';
import {
  COMMAND_LABEL,
  COMMAND_STATUS_LABEL,
  CONTROL_LABEL,
  PROFILE_LABEL,
  bytes,
  dateTime,
  relativeTime,
} from '../lib/labels';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Table,
  errorMessage,
} from '../components/ui';
import { ConnectivityBadge, ProfilePair } from './DevicesPage';

const ALL_CONTROLS = Object.keys(CONTROL_LABEL);

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <>
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-900">{v}</dd>
    </>
  );
}

export function DeviceDetailPage() {
  const { id = '' } = useParams();
  const { tenant, can } = useSession();
  const groups = useResource(() => api.get<Group[]>('/v1/groups', { tenant: true }), [tenant?.id]);
  const device = useResource(
    () => api.get<DeviceDetail>(`/v1/devices/${id}`, { tenant: true }),
    [tenant?.id, id],
  );
  const [sending, setSending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const send = async (type: 'PING' | 'REFRESH_INVENTORY') => {
    setSending(type);
    setActionError(null);
    try {
      // Chave gerada por clique: um duplo clique ou reenvio da mesma requisição não duplica o comando.
      await api.post(
        `/v1/devices/${id}/commands`,
        { type, idempotencyKey: `${type}-${crypto.randomUUID()}` },
        { tenant: true },
      );
      await device.reload();
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setSending(null);
    }
  };

  if (device.error) return <Alert tone="error">{errorMessage(device.error)}</Alert>;
  const d = device.data;
  if (!d) return <p className="text-sm text-slate-500">Carregando…</p>;
  const active = d.status === 'ACTIVE';
  const applied = new Set(d.appliedControls);

  return (
    <>
      <PageHeader
        title={d.name}
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            <ConnectivityBadge c={d.connectivity} /> último contato {relativeTime(d.lastSeenAt)} ·{' '}
            <Link to="/dispositivos" className="text-indigo-700 hover:underline">
              voltar à lista
            </Link>
          </span>
        }
        actions={
          <Button variant="secondary" onClick={() => void device.reload()} busy={device.loading}>
            Atualizar
          </Button>
        }
      />
      {!active && (
        <div className="mb-6">
          <Alert tone="warning">
            Desvinculado {relativeTime(d.revokedAt)}
            {d.revokedReason === 'unenrolled_by_device_user'
              ? ' pelo usuário no próprio aparelho.'
              : '.'}
          </Alert>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Perfil e controles">
          <div className="space-y-4 text-sm">
            <ProfilePair requested={d.requestedProfile} effective={d.effectiveProfile} />
            {d.requestedProfile !== d.effectiveProfile && (
              <Alert tone="info">
                O aparelho opera como <strong>{PROFILE_LABEL[d.effectiveProfile]}</strong>: os
                recursos de {PROFILE_LABEL[d.requestedProfile]} ainda não estão ativos nele.
              </Alert>
            )}
            <ul className="space-y-1" aria-label="Controles">
              {ALL_CONTROLS.map((c) => (
                <li key={c} className="flex items-center justify-between gap-2">
                  <span className={applied.has(c) ? 'text-slate-900' : 'text-slate-400'}>
                    {CONTROL_LABEL[c]}
                  </span>
                  {applied.has(c) ? (
                    <Badge tone="emerald">Aplicado</Badge>
                  ) : (
                    <Badge>Não aplicado</Badge>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500">
              Controles exclusivos de Device Owner (desinstalação, quiosque, restrições, instalação
              silenciosa) só aparecem como aplicados em aparelhos provisionados oficialmente.
            </p>
          </div>
        </Card>

        <Card title="Aparelho">
          <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
            <Row k="Fabricante / modelo" v={`${d.manufacturer} ${d.model}`} />
            <Row k="Android" v={`${d.androidRelease} (API ${d.sdkInt})`} />
            <Row k="Patch de segurança" v={d.securityPatch ?? '—'} />
            <Row k="Versão do agente" v={d.agentVersion} />
            <Row
              k="Bateria"
              v={
                d.telemetry.batteryPercent != null
                  ? `${d.telemetry.batteryPercent}%${d.telemetry.charging ? ' (carregando)' : ''}`
                  : '—'
              }
            />
            <Row
              k="Armazenamento livre"
              v={
                d.telemetry.storageTotalBytes
                  ? `${bytes(d.telemetry.storageFreeBytes)} de ${bytes(d.telemetry.storageTotalBytes)}`
                  : '—'
              }
            />
            <Row k="Rede" v={d.telemetry.network ?? '—'} />
            <Row k="Pareado em" v={dateTime.format(new Date(d.enrolledAt))} />
            <Row k="Grupo" v={d.group?.name ?? '—'} />
          </dl>
        </Card>

        <Card title="Permissões e consentimento">
          <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
            <Row
              k="Notificações"
              v={
                { granted: 'Permitidas', denied: 'Negadas', not_required: 'Não exigidas' }[
                  d.permissions.notifications
                ] ?? d.permissions.notifications
              }
            />
            <Row
              k="Otimização de bateria"
              v={
                d.permissions.batteryOptimizationIgnored
                  ? 'Isento (heartbeat mais regular)'
                  : 'Ativa (heartbeat pode atrasar)'
              }
            />
            <Row k="Localização" v="Não coletada" />
            <Row
              k="Comandos imediatos"
              v={d.pushEnabled ? 'Ativos (push FCM)' : 'Indisponíveis: entrega no próximo contato'}
            />
            <Row
              k="Consentimento"
              v={`${d.consentVersion} · ${dateTime.format(new Date(d.consentAt))}`}
            />
          </dl>
        </Card>
        <AssistedCard
          d={d}
          groups={groups.data ?? []}
          canManage={can('devices:manage')}
          onChanged={() => void device.reload()}
        />

        <Card title="Comandos">
          <div className="space-y-4">
            {actionError && <Alert tone="error">{actionError}</Alert>}
            {active && can('commands:send') && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  busy={sending === 'PING'}
                  onClick={() => void send('PING')}
                >
                  {COMMAND_LABEL.PING}
                </Button>
                <Button
                  variant="secondary"
                  busy={sending === 'REFRESH_INVENTORY'}
                  onClick={() => void send('REFRESH_INVENTORY')}
                >
                  {COMMAND_LABEL.REFRESH_INVENTORY}
                </Button>
              </div>
            )}
            <p className="text-xs text-slate-500">
              Os comandos são entregues no próximo contato do agente (até ~15 min; o Android pode
              atrasar em economia de bateria).
            </p>
            <Table
              head={['Comando', 'Situação', 'Criado', 'Concluído']}
              empty={d.commands.length === 0 && <EmptyState>Nenhum comando.</EmptyState>}
            >
              {d.commands.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-2">{COMMAND_LABEL[c.type] ?? c.type}</td>
                  <td className="px-4 py-2">
                    <Badge
                      tone={
                        c.status === 'SUCCEEDED'
                          ? 'emerald'
                          : c.status === 'FAILED'
                            ? 'red'
                            : c.status === 'EXPIRED' || c.status === 'CANCELED'
                              ? 'slate'
                              : 'amber'
                      }
                    >
                      {COMMAND_STATUS_LABEL[c.status]}
                    </Badge>
                    {c.error && <div className="text-xs text-red-700">{c.error}</div>}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                    {relativeTime(c.createdAt)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                    {relativeTime(c.completedAt)}
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        </Card>

        {active && can('devices:manage') && (
          <Card title="Desvincular" className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <p className="text-slate-600">
                O aparelho deixa de ser aceito pela plataforma e os comandos pendentes são
                cancelados. O app permanece instalado e mostra ao usuário que foi desvinculado.
              </p>
              {confirmRevoke ? (
                <>
                  <Button
                    variant="danger"
                    onClick={async () => {
                      try {
                        await api.post(
                          `/v1/devices/${id}/revoke`,
                          { confirm: true },
                          { tenant: true },
                        );
                        await device.reload();
                      } catch (e) {
                        setActionError(errorMessage(e));
                      }
                      setConfirmRevoke(false);
                    }}
                  >
                    Confirmar desvinculação
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmRevoke(false)}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={() => setConfirmRevoke(true)}>
                  Desvincular dispositivo
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

function AssistedCard({
  d,
  groups,
  canManage,
  onChanged,
}: {
  d: DeviceDetail;
  groups: Group[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const applied = new Set(d.appliedControls);
  const max = Math.max(1, ...d.filterStats.map((f) => f.blocked));
  const row = (label: string, requested: boolean, isApplied: boolean, detail: string) => (
    <li className="flex flex-wrap items-center justify-between gap-2">
      <span className={requested ? 'text-slate-900' : 'text-slate-400'}>
        {label} <span className="text-xs text-slate-500">{requested ? detail : ''}</span>
      </span>
      {!requested ? (
        <Badge>Não pedido</Badge>
      ) : isApplied ? (
        <Badge tone="emerald">Pedido e aplicado</Badge>
      ) : (
        <Badge tone="amber">Pedido, não aplicado</Badge>
      )}
    </li>
  );
  return (
    <Card title="Recursos assistidos" className="lg:col-span-2">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-3 text-sm">
          <p>
            Termo assistido:{' '}
            {d.assistedConsentAccepted ? (
              <Badge tone="emerald">
                Aceito {d.assistedConsentAt ? dateTime.format(new Date(d.assistedConsentAt)) : ''}
              </Badge>
            ) : (
              <Badge tone="amber">Não aceito</Badge>
            )}
          </p>
          <ul className="space-y-2" aria-label="Política pedida e aplicada">
            {row(
              'Tela inicial EndCopilot',
              d.policy.launcherEnabled,
              applied.has('LAUNCHER_HOME'),
              `(${d.policy.allowedApps} app(s)${d.policy.exitPasswordSet ? ', com senha' : ''})`,
            )}
            {row(
              'Filtro de sites',
              d.policy.domainFilterEnabled,
              applied.has('VPN_DOMAIN_FILTER'),
              `(${d.policy.blockedDomains} domínio(s))`,
            )}
          </ul>
          <p className="text-xs text-slate-500">
            "Pedido, não aplicado" é esperado até o usuário aceitar o termo e confirmar no Android
            (escolher a tela inicial, autorizar a VPN). O usuário pode desfazer a qualquer momento.
          </p>
          {canManage && (
            <label className="block">
              <span className="text-sm font-medium text-slate-800">Grupo (define a política)</span>
              <select
                className="mt-1 block w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-slate-300"
                value={d.group?.id ?? ''}
                disabled={saving}
                onChange={async (e) => {
                  setSaving(true);
                  await api
                    .request('PATCH', `/v1/devices/${d.id}`, {
                      body: { groupId: e.target.value || null },
                      tenant: true,
                    })
                    .catch(() => undefined);
                  setSaving(false);
                  onChanged();
                }}
              >
                <option value="">Sem grupo (política da empresa)</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-medium text-slate-800">Bloqueios por dia (últimos 14 dias)</p>
            {d.filterStats.length === 0 ? (
              <p className="text-slate-500">Sem dados.</p>
            ) : (
              <ul className="mt-2 space-y-1" aria-label="Bloqueios por dia">
                {d.filterStats.map((f) => (
                  <li key={f.day} className="flex items-center gap-2">
                    <span className="w-20 text-xs text-slate-500">
                      {f.day.slice(8, 10)}/{f.day.slice(5, 7)}
                    </span>
                    <span
                      className="h-3 rounded bg-indigo-500"
                      style={{ width: `${Math.max(2, (f.blocked / max) * 100)}%` }}
                      aria-hidden
                    />
                    <span className="text-xs tabular-nums text-slate-700">{f.blocked}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs text-slate-500">
              Só a quantidade. Os sites acessados não são enviados.
            </p>
          </div>
          <details>
            <summary className="cursor-pointer font-medium text-slate-800">
              Apps instalados {d.appInventory ? `(${d.appInventory.length})` : '(não enviado)'}
            </summary>
            {d.appInventory && (
              <ul className="mt-2 max-h-48 overflow-y-auto text-xs">
                {d.appInventory.map((a) => (
                  <li key={a.packageName} className="flex justify-between gap-2 py-0.5">
                    <span>{a.label}</span>
                    <span className="truncate font-mono text-slate-500">{a.packageName}</span>
                  </li>
                ))}
              </ul>
            )}
          </details>
        </div>
      </div>
    </Card>
  );
}
